import { Injectable } from "@angular/core";
import { Action, Selector, StateContext, State } from "@ngxs/store";
import { updateItem, patch } from "@ngxs/store/operators";
import { tap, finalize, map } from "rxjs/operators";
import { HttpParams } from "@angular/common/http";

import { PropertiesService } from "../../services/properties/properties.service";
import { Property } from "../../models/property/property.model";
import { Properties } from "./property.actions";
import { PaginatedResult } from "../paginated_result.interface";
import { Filter } from "../../models/filter/filter.model";

export interface PropertyStateModel extends PaginatedResult<Property> {
	selectedProperty?: Property;
}

@State<PropertyStateModel>({
	name: "propertystate",
	defaults: {
		items: [],
		filter: {
			page: 0,
			per_page: 0,
			q: "",
			total: 0,
			f: [],
		},
		loading: false,
	},
})
@Injectable()
export class PropertyState {
	constructor(private propertyService: PropertiesService) {}

	@Selector()
	static filter(state: PropertyStateModel): Filter {
		return state.filter;
	}

	@Selector()
	static getProperties(state: PropertyStateModel) {
		return state.items;
	}

	@Selector()
	static loading(state: PropertyStateModel) {
		return state.loading;
	}

	@Selector()
	static getProperty(state: PropertyStateModel) {
		return (id: number): Property | undefined => state.items.find((p: Property) => p.id === id);
	}

	@Selector()
	static getSelectedProperty(state: PropertyStateModel) {
		return state.selectedProperty;
	}

	@Selector()
	static filterActive(state: PropertyStateModel) {
		return !(state.filter.f?.length == 0 && state.filter.q == "");
	}

	@Action(Properties.SetFilter)
	setFilter(ctx: StateContext<PropertyStateModel>, action: Properties.SetFilter) {
		return ctx.setState(
			patch<PropertyStateModel>({
				filter: action.filter,
			})
		);
	}

	@Action(Properties.FetchAll)
	fetchProperties(ctx: StateContext<PropertyStateModel>) {
		const state = ctx.getState();
		let filter: any = state.filter
			? Object.entries(state.filter).reduce((acc, [key, value]) => {
					acc[key] = value === null ? "" : value;
					return acc;
			  }, {} as any)
			: {};

		const options = {
			params: new HttpParams({
				fromObject: filter,
			}),
		};

		ctx.patchState({ loading: true });

		return this.propertyService.fetchProperties(options).pipe(
			tap((returnData: any) => {
				ctx.patchState({
					...ctx.getState(),
					items: returnData.data,
					filter: {
						...ctx.getState().filter,
						page: returnData.current_page,
						per_page: returnData.per_page,
						total: returnData.total,
					},
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Properties.FecthProperty)
	fetchProperty(ctx: StateContext<PropertyStateModel>, action: Properties.FecthProperty) {
		ctx.patchState({
			loading: true,
		});

		return this.propertyService.fetchProperty(action.propertyId).pipe(
			tap((returnData: any) => {
				ctx.setState(
					patch({
						...ctx.getState(),
						selectedProperty: returnData.data,
					})
				);
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Properties.Add)
	addProperty(ctx: StateContext<PropertyStateModel>, action: Properties.Add) {
		ctx.patchState({
			loading: true,
		});
		return this.propertyService.addProperties(action.property).pipe(
			tap((returnData: any) => {
				const newProperty = returnData.data;
				ctx.patchState({
					selectedProperty: newProperty,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Properties.Update)
	updateProperty(ctx: StateContext<PropertyStateModel>, action: Properties.Update) {
		ctx.patchState({
			loading: true,
		});
		return this.propertyService.updateProperties(action.propertyId, action.property).pipe(
			tap((returnData: any) => {
				const updated = returnData.data;
				const state = ctx.getState();

				ctx.patchState({
					items: state.items.map((p) => (p.id === action.propertyId ? { ...p, ...updated } : p)),
					selectedProperty:
						state.selectedProperty?.id === action.propertyId
							? { ...state.selectedProperty, ...updated }
							: state.selectedProperty,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(Properties.Delete)
	deleteProperty(ctx: StateContext<PropertyStateModel>, action: Properties.Delete) {
		ctx.patchState({
			loading: true,
		});
		return this.propertyService.deleteProperty(action.propertyId).pipe(
			tap((returnData: any) => {
				const state = ctx.getState();
				ctx.patchState({
					items: state.items.filter((p: Property) => p.id !== action.propertyId),
					selectedProperty: state.selectedProperty?.id === action.propertyId ? undefined : state.selectedProperty,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}
}
